/**
 * Matrix algebra
 */
import { curry, reduce, head, tail, is, prop, join, map } from 'ramda';

/**
 * Multiply a x b where a and b are each an array of row-ordered numbers.
 * If a is an m x n matrix, then b must be an n x p matrix
 *
 * @sig Matrix a -> Matrix b -> Matrix c
 *      Matrix = Array(Array(Number), Array(Number), Array(Number))
 *
 * @example
 *
 *     const a = [
 *         [1, 2, 3],
 *         [4, 5, 6],
 *         [7, 8, 9]
 *     ]
 *
 *     const b = [
 *         [1, 1, 1],
 *         [1, 1, 1],
 *         [1, 1, 1]
 *     ]
 *
 *     const c = [
 *         [1],
 *         [1],
 *         [1]
 *     ]
 *
 *     multiply(a,b) =>
 *        [
 *            [6,   6,  6],
 *            [15, 15, 15],
 *            [24, 24, 24]
 *        ]
 *
 *     multiply(a,c) =>
 *        [
 *            [6],
 *            [15],
 *            [24]
 *        ]
 *
 * @param {Array} a - left side
 * @param {Array} b - right side
 *
 */
const multiply = curry((a, b) => {
    const aNumRows = a.length;
    const aNumCols = a[0].length;
    const bNumCols = b[0].length;
    const m = new Array(aNumRows); // initialize array of rows

    for (let r = 0; r < aNumRows; ++r) {
        m[r] = new Array(bNumCols); // initialize the current row
        for (let c = 0; c < bNumCols; ++c) {
            m[r][c] = 0; // initialize the current cell
            for (let i = 0; i < aNumCols; ++i) {
                m[r][c] += a[r][i] * b[i][c];
            }
        }
    }

    return m;
});

/*
 * Combine matrices left to right to get a new output matrix
 * @sig concatenate :: ...Matrix -> Matrix
 */
const concatenate = (...matrices) => reduce((acc, m) => multiply(acc, m), head(matrices), tail(matrices));

/**
 * Returns a translation matrix from dx and dy
 *
 * @sig (Number, Number) => Matrix
 * @param {Number} dx
 * @param {Number} dy
 * @return Array
 */
const translationMatrix = (dx, dy) => [
    [1, 0, dx],
    [0, 1, dy],
    [0, 0, 1],
];

/**
 * Returns a scaling matrix from s
 *
 * @sig (Number) => Matrix
 * @param {Number} s - the scale for both x and y directions
 * @return Array
 */
const scalingMatrix = s => [
    [s, 0, 0],
    [0, s, 0],
    [0, 0, 1],
];

/**
 * Translate matrix m by (dx, dy)
 *
 * @sig (Number, Number) -> Matrix -> Matrix
 */
const translate = curry((dx, dy, m) => multiply(translationMatrix(dx, dy), m));

/**
 * Returns a rotation matrix from degrees (clockwise)
 *
 * @sig Number -> Matrix
 */
const rotatingMatrix = degrees => {
    const degreesToRadians = degrees => degrees * (Math.PI / 180);

    const radians = degreesToRadians(degrees);

    return [
        [Math.cos(radians), -Math.sin(radians), 0],
        [Math.sin(radians), Math.cos(radians), 0],
        [0, 0, 1],
    ];
};

/**
 * Translate matrix m by (dx, dy)
 *
 * @sig Number -> Matrix -> Matrix
 */
const rotate = curry((degrees, m) => multiply(rotatingMatrix(degrees), m));

/**
 * Scale matrix m by s
 *
 * @sig (Number) -> Matrix -> Matrix
 */
const scale = curry((s, m) => multiply(scalingMatrix(s), m));

/*
 * Rotate x, y, degrees around centerX, centerY:
 * - translate center point to origin
 * - rotate by degree
 * - translate back to center
 */
const rotateAroundPoint = (x, y, centerX, centerY, degrees) => {
    const toCenter = translationMatrix(-centerX, -centerY);
    const rotation = rotatingMatrix(degrees);
    const fromCenter = translationMatrix(centerX, centerY);
    const p = pointToMatrix(x, y);
    const composite = concatenate(fromCenter, rotation, toCenter, p);

    return matrixToPointArray(composite);
};

const isNumber = is(Number);
const isArray = is(Array);

/**
 * Translate a point provided in one of three formats into an matrix
 *
 * @sig:     (Number a, Number b) -> [[a], [b], [1]]  OR
 *           {a:, b:}             -> [[a], [b], [1]]  OR
 *           [a, b]               -> [[a], [b], [1]]
 * @param {Number|Array|Object} a
 * @param {Number} [b]
 * @returns {Array|undefined}
 */
const standardizePoint = (a, b) => {
    if (isArray(a)) return a;
    if (isNumber(a)) return [a, b];
    if (isNumber(prop('x', a))) return [a.x, a.y];
    return undefined;
};

/**
 * Convert a matrix to an array whose first value is x and second value is y
 * @sig   Matrix -> Array
 * @param m
 */
const matrixToPointArray = m => [m[0][0], m[1][0]];

/**
 * Convert a matrix to an object with x and y keys
 * @sig   Matrix -> {k:v}
 * @param m
 */
const matrixToPointObject = m => ({ x: m[0][0], y: m[1][0] });

/**
 *  a, c, e
 *  b, d, f       =>  [a, b, c, d, e, f]
 *  0, 0, 1
 *
 * See http://www.useragentman.com/blog/2011/01/07/css3-matrix-transform-for-the-mathematically-challenged/
 *
 * @param m
 * @returns {Array}
 */
const toCssTransform = m => [
    m[0][0], //  horizontal scale
    m[1][0], //  horizontal skew
    m[0][1], //  vertical skew
    m[1][1], //  vertical scale
    m[0][2], //  horizontal transform
    m[1][2], //  vertical transform
];

/**
 * Return an identity matrix of the given width/height
 *
 * @param dimension
 * @returns {Array}
 */
const identityMatrix = dimension => {
    const result = [];
    let i;

    for (i = 0; i < dimension; i++) result.push(Array(dimension).fill(0));
    for (i = 0; i < dimension; i++) result[i][i] = 1;

    return result;
};

/**
 * Convert a point to a matrix with 3 rows and one column. Uses standardizePoint to convert multiple formats
 *
 * @sig:     (Number a, Number b) -> [[a], [b], [1]]  OR
 *           {a:, b:}             -> [[a], [b], [1]]  OR
 *           [a, b]               -> [[a], [b], [1]]
 * @param x
 * @param y
 */
const pointToMatrix = (x, y) => [[standardizePoint(x, y)[0]], [standardizePoint(x, y)[1]], [1]];

/**
 * Convert a number to a matrix with 3 rows and one column.
 *
 * @sig:     Number a -> [[a], [a], [1]]
 * @param x
 */
const numberToMatrix = x => pointToMatrix(x, x);

// TODO: move to a centralized location and reorganize
const padLeft = (char, padWidth, number) =>
    Array(Math.max(0, padWidth - String(number).length + 1)).join(char) + number;
const toFixed = (decimals, n) => n.toFixed(decimals);
const padToFixed = (char, padWidth, decimals) => n => padLeft(char, padWidth, toFixed(decimals, n));

const rowToString = row => join(' ', map(padToFixed(' ', 6, 1), row));
const toString = matrix => map(rowToString, matrix).join('\n');

export {
    // [x, y] <=> matrix
    pointToMatrix,
    matrixToPointArray,

    // x <=> matrix
    numberToMatrix,
    matrixToPointObject,

    // basic multiply
    multiply,
    concatenate,

    // generate a 3 x 3 matrix
    identityMatrix,
    scalingMatrix,
    translationMatrix,
    rotatingMatrix,

    // shorthand for multiply(matrix * point)
    translate,
    scale,
    rotate,
    rotateAroundPoint,

    // to string
    toCssTransform,
    toString,
};
